<template>
  <section>
    <notifications></notifications>

    <base-header>
      <div class="row align-items-center pt-header">
        <div class="col-12 d-flex align-items-center">
          <el-tooltip content="Back" placement="bottom">
            <base-button
              type="neutral"
              class="btn-icon-only"
              @click="goToPreviousPage()"
            >
              <i class="flaticon-left-1"></i>
            </base-button>
          </el-tooltip>

          <h1 class="title-view capitalize">
            <template v-if="branch">
              <template v-if="supplierName">{{ supplierName }} · </template>
              <span class="text-muted map-title-branch">{{ branch.name }}</span>
            </template>
            <template v-else>{{ $route.meta.title }}</template>
          </h1>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mb-5">
      <div class="card">
        <div class="card-body">
          <div class="row">
            <div class="col-xl-3 col-lg-4 col-12">
              <base-input label="Date Range">
                <flat-picker
                  slot-scope="{ focus, blur }"
                  @on-open="focus"
                  @on-close="blur"
                  @on-change="onDateChange"
                  :config="{ allowInput: true, mode: 'range' }"
                  class="input-custom"
                  placeholder="Enter Date Range"
                  v-model="range"
                ></flat-picker>
              </base-input>

              <base-alert v-if="truncated" type="warning" class="mb-3">
                Showing the first {{ reservations.length }} of {{ total }}
                reservations. Narrow the date range to see all of them.
              </base-alert>

              <div class="map-legend mb-3">
                <div class="map-legend-row">
                  <b-form-checkbox v-model="showReservations">
                    <strong
                      >Reservations
                      <small class="map-legend-count"
                        >({{ plottedReservations }})</small
                      ></strong
                    >
                    <div
                      v-if="skippedReservations > 0"
                      class="small text-muted map-legend-description"
                    >
                      {{ skippedReservations }} without destination coordinates
                    </div>
                  </b-form-checkbox>
                </div>
                <div class="map-legend-row d-flex justify-content-between">
                  <strong
                    >Coverage Areas
                    <small class="map-legend-count"
                      >({{ areas.length }})</small
                    ></strong
                  >
                  <span
                    v-if="areas.length"
                    class="link small"
                    @click="toggleAllAreas"
                  >
                    {{ allAreasVisible ? "Hide all" : "Show all" }}
                  </span>
                </div>
                <div v-if="!areas.length" class="text-muted small">
                  There are no coverage areas for this destination
                </div>
                <div
                  v-for="(area, index) in areas"
                  :key="area._id"
                  class="map-legend-row"
                >
                  <b-form-checkbox v-model="visibleAreaIds" :value="area._id">
                    <span
                      class="map-swatch"
                      :style="{ backgroundColor: getAreaColor(index) }"
                    ></span>
                    <strong>{{ area.code }}</strong>
                    <div class="small text-muted map-legend-description">
                      {{ area.description }}
                    </div>
                  </b-form-checkbox>
                </div>
              </div>
            </div>
            <div class="col-xl-9 col-lg-8 col-12">
              <DestinationMap
                :center="center"
                :areas="areas"
                :reservations="reservations"
                :visibleAreaIds="visibleAreaIds"
                :showReservations="showReservations"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { BranchesAPI } from "../../../core/api";
import { goBack, setSigOut } from "../../../helpers/commons.helpers";
import DestinationMap from "../components/destinations/DestinationMap.vue";
import {
  getAreaColor,
  getDefaultDateRange,
  getReservationPoints,
  hasValidCoordinates,
  toDateQuery
} from "../services/map.service";

const LOAD_ERROR_MESSAGE = "The map data could not be loaded. Please try again";

/**
 * Supplier destination map: coverage areas and transfer reservations of one branch,
 * filtered by pick-up date range. Internal tool, shows quoted (net) prices.
 */
export default {
  name: "SupplierDestinationMap",
  components: {
    flatPicker,
    DestinationMap
  },
  data() {
    return {
      range: getDefaultDateRange(),
      branch: null,
      areas: [],
      reservations: [],
      total: 0,
      truncated: false,
      visibleAreaIds: [],
      showReservations: true
    };
  },
  created() {
    this.loadBranchMap();
  },
  methods: {
    getAreaColor,

    /* ------------------------------ Data ------------------------------ */

    /**
     * Load the branch map for the selected range and update the page state.
     */
    async loadBranchMap() {
      const date = toDateQuery(this.range);
      if (!date) return;
      try {
        this.setLoad(true);
        const data = await this.fetchBranchMap(date);
        if (data.status) {
          this.setBranchMap(data.data);
        } else {
          this.notifyError(LOAD_ERROR_MESSAGE);
        }
      } catch (error) {
        this.handleError(error, LOAD_ERROR_MESSAGE);
      } finally {
        this.setLoad(false);
      }
    },
    /**
     * Request the branch map to the API.
     * @param {string} date - Backend date param (YYYY-MM-DDtoYYYY-MM-DD).
     * @returns {Promise<Object>} API response body.
     */
    async fetchBranchMap(date) {
      const query = new URLSearchParams({ date: date }).toString();
      const { data } = await BranchesAPI.getBranchMap(
        this.branchId,
        query,
        this.sesion.token
      );
      return data;
    },
    /**
     * Store the API payload and show every area by default.
     * @param {{branch: Object, areas: Array, reservations: Array, total: number, truncated: boolean}} payload
     */
    setBranchMap({ branch, areas, reservations, total, truncated }) {
      this.branch = branch;
      this.areas = areas || [];
      this.reservations = reservations || [];
      this.total = total || 0;
      this.truncated = !!truncated;
      this.visibleAreaIds = this.getAllAreaIds();
    },

    /* ----------------------------- Filters ----------------------------- */

    /**
     * Reload once the range is complete (flatpickr also emits on the first click).
     * @param {Array<Date>} selectedDates
     */
    onDateChange(selectedDates) {
      if (selectedDates.length === 2) {
        this.$nextTick(() => this.loadBranchMap());
      }
    },
    /**
     * Show every area, or hide them all when they are already visible.
     */
    toggleAllAreas() {
      this.visibleAreaIds = this.allAreasVisible ? [] : this.getAllAreaIds();
    },
    /**
     * @returns {Array<string>} Ids of every loaded area.
     */
    getAllAreaIds() {
      return this.areas.map(area => area._id);
    },

    /* ----------------------------- Errors ----------------------------- */

    /**
     * Sign out on 401, otherwise show the error notification.
     * @param {Error} error - Axios error.
     * @param {string} message - Message shown to the user.
     */
    handleError(error, message) {
      const isUnauthorized =
        error.response && error.response.data.code === 401;
      if (isUnauthorized) {
        if (setSigOut(error)) {
          this.setSigOut(true);
        }
        return;
      }
      this.notifyError(message);
    },
    /**
     * @param {string} message - Message shown to the user.
     */
    notifyError(message) {
      this.$notify({
        type: "danger",
        title: "Error!",
        message: message,
        icon: "flaticon-remove"
      });
    },

    /* --------------------------- Navigation --------------------------- */

    goToPreviousPage() {
      goBack(this);
    },
    ...mapMutations(["setSigOut", "setLoad"])
  },
  computed: {
    /** @returns {string} Branch id from the route. */
    branchId() {
      return this.$route.params.branchId;
    },
    /** @returns {string} Supplier business name, empty while loading. */
    supplierName() {
      const supplier = this.branch && this.branch.supplier;
      return supplier && supplier.businessDetails
        ? supplier.businessDetails.name
        : "";
    },
    /** @returns {?{lat: number, lng: number}} Branch location used to centre an empty map. */
    center() {
      const location =
        this.branch && this.branch.geometry && this.branch.geometry.location;
      return hasValidCoordinates(location) ? location : null;
    },
    /** @returns {Array<{from: ?Object, to: ?Object}>} Valid points of every reservation. */
    reservationPoints() {
      return this.reservations.map(getReservationPoints);
    },
    /** @returns {number} Reservations drawn on the map (valid origin). */
    plottedReservations() {
      return this.reservationPoints.filter(points => points.from).length;
    },
    /** @returns {number} Reservations without destination coordinates. */
    skippedReservations() {
      return this.reservationPoints.filter(points => !points.to).length;
    },
    /** @returns {boolean} Whether every area is currently shown. */
    allAreasVisible() {
      return (
        this.areas.length > 0 &&
        this.visibleAreaIds.length === this.areas.length
      );
    },
    ...mapGetters(["sesion"])
  }
};
</script>

<style scoped>
.map-legend {
  /* Map height minus the date range input, so both columns end at the same line. */
  max-height: calc(75vh - 90px);
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.75rem;
}
.map-legend-row {
  padding: 0.35rem 0;
  border-bottom: 1px solid #f1f3f5;
}
.map-legend-row:last-child {
  border-bottom: none;
}
/* The theme fixes the checkbox label to 1rem; let it grow with long descriptions. */
.map-legend >>> .custom-control-label {
  height: auto;
  white-space: normal;
}
/* Same font and size as the title, only the colour changes. */
.map-title-branch {
  font: inherit;
}
/* Only the number is smaller; the title keeps its size. */
.map-legend-count {
  font-size: 75%;
}
.map-legend-description {
  line-height: 1.3;
  margin-top: 2px;
  word-break: break-word;
}
.map-swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-right: 6px;
  border-radius: 2px;
  vertical-align: middle;
}
.link {
  cursor: pointer;
}
</style>
